import type React from "react";
import { Box, Typography } from "@mui/material";
import PieChartOutlineOutlinedIcon from "@mui/icons-material/PieChartOutlineOutlined";
import { useTranslation } from "react-i18next";
import DetailSectionCard from "../common/DetailSectionCard";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { DashboardExpenseType, DashboardExpenseTypeValue } from "../../types/dashboard";
import { formatRupiah } from "../../utils/formatRupiah";

type TranslationKeys = keyof typeof translations.en;

const EXPENSE_ROWS: Array<{
  type: DashboardExpenseType;
  labelKey: TranslationKeys;
  color: string;
}> = [
  { type: "uang_jalan", labelKey: "Uang Jalan", color: "#3B82F6" },
  { type: "mob", labelKey: "dashboard.expenseMob", color: "#0EA5E9" },
  { type: "taktis", labelKey: "dashboard.expenseTaktis", color: "#8B5CF6" },
  { type: "registration", labelKey: "dashboard.expenseRegistration", color: "#64748B" },
  { type: "standby", labelKey: "dashboard.expenseStandby", color: "#F59E0B" },
  { type: "additional", labelKey: "dashboard.expenseAdditional", color: "#EF4444" },
];

interface DashboardExpenseMixProps {
  expensesByType: Record<DashboardExpenseType, DashboardExpenseTypeValue>;
}

const DashboardExpenseMix: React.FC<DashboardExpenseMixProps> = ({ expensesByType }) => {
  const { t } = useTranslation();

  const totalPaid = EXPENSE_ROWS.reduce((sum, row) => sum + expensesByType[row.type].paid, 0);
  const maxPaid = Math.max(totalPaid, 1);

  return (
    <DetailSectionCard
      title={t("dashboard.expenseMix" as TranslationKeys)}
      icon={<PieChartOutlineOutlinedIcon fontSize="small" />}
      iconColor="#16A34A"
      data-testid={TestId.DASHBOARD.EXPENSE_MIX}
    >
      {totalPaid === 0 ? (
        <Typography color="text.secondary">
          {t("dashboard.noActivityInRange" as TranslationKeys)}
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {EXPENSE_ROWS.map((row) => {
            const paid = expensesByType[row.type].paid;
            const pct = (paid / maxPaid) * 100;

            return (
              <Box key={row.type}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 2,
                    mb: 0.75,
                  }}
                >
                  <Typography variant="body2">{t(row.labelKey)}</Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatRupiah(paid)}
                  </Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: 1, bgcolor: "divider" }}>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      width: `${pct}%`,
                      bgcolor: row.color,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </DetailSectionCard>
  );
};

export default DashboardExpenseMix;
